import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

export default function LoginPage({ onBack }) {
  const { login, loginAsDemo } = useAuth();
  const { theme } = useTheme();
  const isDark = theme.mode === 'dark';

  const terra = '#c45e3e';
  const forest = '#1c3a2e';
  const ivory = '#faf7f2';
  const cardBg = isDark ? '#1e1a17' : '#ffffff';
  const textMain = isDark ? '#f5ede0' : '#1c1208';
  const textSub = isDark ? '#8a7a60' : '#6b5040';
  const pageBg = isDark ? '#141210' : '#faf7f2';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handle = async () => {
    if (!username || !password) { setError('Please enter your username and password'); return; }
    setLoading(true); setError('');
    try {
      await login(username, password);
    } catch (e) {
      setError('Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  return (
    <div style={{ minHeight:'100vh', background:pageBg, display:'flex', position:'relative', overflow:'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,700;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');
        * { box-sizing: border-box; }
        .login-input:focus { border-color: ${terra} !important; box-shadow: 0 0 0 3px rgba(196,94,62,0.12) !important; outline: none !important; }
        @keyframes fadeIn { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        @keyframes slideIn { from { opacity:0; transform:translateX(-30px); } to { opacity:1; transform:translateX(0); } }
        .fade-in { animation: fadeIn 0.7s cubic-bezier(0.16,1,0.3,1) forwards; }
        .slide-in { animation: slideIn 0.9s cubic-bezier(0.16,1,0.3,1) forwards; }
      `}</style>

      {/* Left decorative panel */}
      <div className="slide-in" style={{
        display:'none',
        position:'fixed',
        left:0, top:0, bottom:0, width:'42%',
        background:`linear-gradient(160deg, ${forest} 0%, #0d2218 100%)`,
        flexDirection:'column',
        justifyContent:'center',
        padding:'60px 56px',
        zIndex:1,
      }}
      id="left-panel"
      >
        {/* Organic pattern overlay */}
        <div style={{ position:'absolute', inset:0, opacity:0.06, backgroundImage:'radial-gradient(circle at 20% 30%, #f4a261 0%, transparent 50%), radial-gradient(circle at 80% 70%, #c45e3e 0%, transparent 50%)' }} />
        <div style={{ position:'absolute', bottom:-80, left:-80, width:300, height:300, borderRadius:'50%', border:'1px solid rgba(244,162,97,0.15)' }} />
        <div style={{ position:'absolute', bottom:-20, left:-20, width:160, height:160, borderRadius:'50%', border:'1px solid rgba(244,162,97,0.1)' }} />
        <div style={{ position:'absolute', top:40, right:-60, width:200, height:200, borderRadius:'50%', border:'1px solid rgba(196,94,62,0.12)' }} />

        <div style={{ position:'relative' }}>
          {theme.logoUrl ? (
            <img src={theme.logoUrl} alt="logo" style={{ height:50, marginBottom:32, objectFit:'contain' }} />
          ) : (
            <div style={{ fontSize:44, color:'#f4a261', fontFamily:"'Playfair Display', serif", marginBottom:24, lineHeight:1 }}>{theme.hotelSymbol || '◈'}</div>
          )}
          <div style={{ fontSize:10, letterSpacing:5, color:'rgba(244,162,97,0.7)', textTransform:'uppercase', marginBottom:8 }}>Welcome to</div>
          <h1 style={{ fontSize:42, fontFamily:"'Playfair Display', serif", fontWeight:600, color:'#f5ede0', lineHeight:1.15, marginBottom:8 }}>
            {theme.hotelName || 'Grand Memories Resort & Spa'}
          </h1>
          <div style={{ fontSize:13, color:'rgba(244,162,97,0.8)', letterSpacing:3, textTransform:'uppercase', marginBottom:40 }}>{theme.hotelTagline || ''}</div>
          <div style={{ width:48, height:2, background:terra, marginBottom:36 }} />
          <p style={{ fontSize:15, color:'rgba(245,237,224,0.65)', lineHeight:1.9, fontWeight:300 }}>
            The integrated management platform for {theme.hotelName || 'Grand Memories Resort & Spa'} — designed for elegance, built for efficiency.
          </p>
          <div style={{ marginTop:48, display:'flex', flexDirection:'column', gap:14 }}>
            {[['🌿','Staff Portal','Secure access for all departments'],['🔒','Role-Based Access','Each role sees only what they need'],['📊','Live Dashboard','Real-time data across the property']].map(([icon,t,d]) => (
              <div key={t} style={{ display:'flex', alignItems:'center', gap:14, opacity:0.7 }}>
                <span style={{ fontSize:20 }}>{icon}</span>
                <div>
                  <div style={{ fontSize:12, color:'#f5ede0', fontWeight:600 }}>{t}</div>
                  <div style={{ fontSize:11, color:'rgba(245,237,224,0.5)' }}>{d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right / main login section */}
      <div className="fade-in" style={{
        flex:1,
        display:'flex',
        flexDirection:'column',
        alignItems:'center',
        justifyContent:'center',
        padding:'40px 24px',
        minHeight:'100vh',
      }}>
        {/* Mobile-only header */}
        <div style={{ marginBottom:32, textAlign:'center' }}>
          {theme.logoUrl ? (
            <img src={theme.logoUrl} alt="logo" style={{ height:44, marginBottom:12, objectFit:'contain' }} />
          ) : (
            <div style={{ fontSize:40, color:terra, fontFamily:"'Playfair Display', serif", marginBottom:4 }}>{theme.hotelSymbol || '◈'}</div>
          )}
          <div style={{ fontSize:18, fontFamily:"'Playfair Display', serif", fontWeight:600, color:textMain, letterSpacing:1 }}>{theme.hotelName || 'Grand Memories Resort & Spa'}</div>
          <div style={{ fontSize:10, color:textSub, letterSpacing:3, textTransform:'uppercase', marginTop:2 }}>Staff Portal</div>
        </div>

        {/* Login card */}
        <div style={{
          background:cardBg,
          borderRadius:16,
          padding:'36px 32px',
          width:'100%',
          maxWidth:400,
          boxShadow: isDark ? '0 20px 60px rgba(0,0,0,0.4)' : '0 8px 40px rgba(28,18,8,0.12)',
          border: isDark ? '1px solid rgba(196,94,62,0.2)' : '1px solid rgba(196,94,62,0.12)',
        }}>
          <div style={{ marginBottom:24 }}>
            <div style={{ fontSize:22, fontFamily:"'Playfair Display', serif", fontWeight:600, color:textMain, marginBottom:4 }}>Sign In</div>
            <div style={{ fontSize:13, color:textSub }}>Enter your credentials to access the system</div>
          </div>

          <div style={{ marginBottom:16 }}>
            <label style={{ display:'block', fontSize:11, fontWeight:600, color:textSub, letterSpacing:1.2, textTransform:'uppercase', marginBottom:6 }}>Username</label>
            <input
              className="login-input"
              style={{ width:'100%', background:isDark?'#2a2420':'#faf7f2', border:`1.5px solid ${isDark?'#3a3028':'#e8ddd0'}`, borderRadius:8, padding:'11px 14px', color:textMain, fontSize:14, fontFamily:'inherit', boxSizing:'border-box', transition:'all .2s' }}
              placeholder="Your username"
              value={username}
              onChange={e => setUsername(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handle()}
            />
          </div>

          <div style={{ marginBottom:20 }}>
            <label style={{ display:'block', fontSize:11, fontWeight:600, color:textSub, letterSpacing:1.2, textTransform:'uppercase', marginBottom:6 }}>Password</label>
            <div style={{ position:'relative' }}>
              <input
                className="login-input"
                style={{ width:'100%', background:isDark?'#2a2420':'#faf7f2', border:`1.5px solid ${isDark?'#3a3028':'#e8ddd0'}`, borderRadius:8, padding:'11px 44px 11px 14px', color:textMain, fontSize:14, fontFamily:'inherit', boxSizing:'border-box', transition:'all .2s' }}
                placeholder="Your password"
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handle()}
              />
              <button style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', fontSize:16, color:textSub, padding:0 }} onClick={() => setShowPw(!showPw)}>
                {showPw ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          {error && (
            <div style={{ background:'rgba(214,64,69,0.1)', border:'1px solid rgba(214,64,69,0.3)', borderRadius:8, padding:'10px 14px', color:'#d64045', fontSize:13, marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
              <span>⚠</span> {error}
            </div>
          )}

          <button
            style={{ width:'100%', background:`linear-gradient(135deg, ${terra}, #a04828)`, border:'none', borderRadius:8, padding:'13px', color:'#fff', fontWeight:700, fontSize:14, cursor:'pointer', fontFamily:'inherit', letterSpacing:0.5, transition:'opacity .2s', opacity:loading?0.7:1, boxShadow:`0 4px 16px rgba(196,94,62,0.35)` }}
            onClick={handle} disabled={loading}
          >
            {loading ? 'Signing in…' : 'Sign In →'}
          </button>

          {/* Divider */}
          <div style={{ display:'flex', alignItems:'center', gap:10, margin:'14px 0 2px' }}>
            <div style={{ flex:1, height:1, background:isDark?'#3a3028':'#e8ddd0' }} />
            <span style={{ fontSize:11, color:textSub, letterSpacing:1 }}>OR</span>
            <div style={{ flex:1, height:1, background:isDark?'#3a3028':'#e8ddd0' }} />
          </div>

          {/* Demo button */}
          <button
            style={{
              width:'100%', background:'none',
              border:`1.5px solid ${terra}`,
              borderRadius:8, padding:'12px', color:terra,
              fontWeight:700, fontSize:13, cursor:'pointer',
              fontFamily:'inherit', marginTop:8, letterSpacing:0.3,
              transition:'all .2s', opacity:loading?0.6:1,
            }}
            onMouseEnter={e => { e.currentTarget.style.background=`rgba(196,94,62,0.08)`; }}
            onMouseLeave={e => { e.currentTarget.style.background='none'; }}
            onClick={async () => {
              setLoading(true); setError('');
              try { await loginAsDemo(); }
              catch { setError('Demo unavailable. Please try again.'); }
              finally { setLoading(false); }
            }}
            disabled={loading}
          >
            🎯 Try Demo — Read-Only Preview
          </button>

          {onBack && (
            <button style={{ width:'100%', background:'none', border:`1px solid ${isDark?'#3a3028':'#e8ddd0'}`, borderRadius:8, padding:'10px', color:textSub, fontSize:12, cursor:'pointer', fontFamily:'inherit', marginTop:8, transition:'all .2s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor=terra; e.currentTarget.style.color=terra; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor=isDark?'#3a3028':'#e8ddd0'; e.currentTarget.style.color=textSub; }}
              onClick={onBack}
            >
              ← Back to Hotel Website
            </button>
          )}
        </div>


      </div>

      {/* CSS to show left panel on large screens */}
      <style>{`
        @media (min-width: 900px) {
          #left-panel { display:flex !important; }
          .fade-in { margin-left:42%; }
        }
      `}</style>
    </div>
  );
}
